import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components/macro';

import CloseIconM from 'web-ui-kit/assets/CloseIcon';
import { Box, Flex } from 'web-ui-kit/containers';
import { white, semiTransparentBlack } from 'web-ui-kit/colors';
import { Modal } from 'web-ui-kit/modal';
import {
  MQ_FOR_PHONE_ONLY,
  MQ_FOR_PHONE_WIDTH
} from 'web-ui-kit/media';
import { RoundedSpinner } from 'web-ui-kit/loading';
import { BodyLargeStyles, Header2LargeStyles } from 'web-ui-kit/typography';
import PrimaryButton from 'web-ui-kit/buttons/PrimaryButton';
import SecondaryButton from 'web-ui-kit/buttons/SecondaryButton';

import { H2, H3, H3Fonts, BodyFonts } from 'shared/fontStyles/StudioHeadings';

export const SWAP_MULT_MODAL_ID = 'swap-multiple-modal';

export default function SwapMultipleModal (props) {
  return ReactDOM.createPortal(
    <SwapMultipleModalContent {...props} />,
    document.body
  );
}

export const SwapMultipleModalContent = ({
  onToggleModal = () => {},
  onSwapOneClick = () => {},
  onSwapAllClick = () => {},
  swapInProgress = false
} = {}) => {
  const renderButtons = () => {
    return (
      <div style={{ display: 'flex' }}>
        <SwapOneButton
          id={'swap-one'}
          dispatch={(event) => {
            const { type } = event;

            if (type === 'swap-one-button-clicked') {
              onSwapOneClick();
            }
          }}
          disabled={swapInProgress}
          labelText={swapInProgress ? 'Swapping In' : 'Swap One'}
        />
        <SwapAllButton
          id={'swap-all'}
          dispatch={(event) => {
            const { type } = event;

            if (type === 'swap-all-button-clicked') {
              onSwapAllClick();
            }
          }}
          disabled={swapInProgress}
          labelText={swapInProgress ? 'Swapping In' : 'Swap All'}
        />
      </div>
    );
  };

  return (
    <Modal bgColor={semiTransparentBlack} center id={SWAP_MULT_MODAL_ID}>
      <Container>
        <CloseBox
          onClick={onToggleModal}
        >
          <CloseIconM size={'48'} />
        </CloseBox>

        <ModalTitle>
          Replace all?
        </ModalTitle>

        <Paragraph>
          {`There’s more than one of these in your design.\nWould you like to replace them all?`}
        </Paragraph>

        {renderButtons()}
      </Container>
    </Modal>
  );
};

/** Styles */
const Container = styled(Flex)`
  align-items: center;
  justify-content: center;
  flex-direction: column;

  overflow-y: scroll;
  overflow-x: hidden;

  position: relative;

  background-color: ${white};
  padding: 48px 40px;

  ${MQ_FOR_PHONE_WIDTH} {
    padding: 40px 20px;
    margin: 0 16px;
  }

  padding: 1rem;
`;
Container.displayName = 'Container';

const CloseBox = styled(Box)`
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 0;
`;
CloseBox.displayName = 'CloseBox';

const ModalTitle = styled(H2)`
  margin-bottom: 40px;
  text-align: center;
  width: 100%;

  ${MQ_FOR_PHONE_ONLY} {
    ${H3Fonts}
    margin-bottom: 16px;
  }

  ${Header2LargeStyles};
  margin-top: 2rem;
`;
ModalTitle.displayName = 'ModalTitle';

const Paragraph = styled(H3)`
  margin: 0 20px 32px;
  text-align: center;
  white-space: pre-wrap;
  width: 100%;

  ${MQ_FOR_PHONE_ONLY} {
    ${BodyFonts}
    margin: 0 0 24px;
    white-space: pre;
  }

  ${BodyLargeStyles};
  margin: 1rem auto 2.5rem;
`;
Paragraph.displayName = 'Paragraph';

const CtaFlex = styled(Flex)`
  flex-direction: row;
  height: 58px;
  width: 100%;
  max-width: 450px;

  > :first-child {
    margin-right: 10px;
  }
`;
CtaFlex.displayName = 'CtaFlex';

const RoundedCTASpinner = styled(RoundedSpinner)`
  margin-right: 8px;

  & span {
    display: block;
    position: absolute;
    height: 1px;
    width: 1px;
  }
`;
RoundedCTASpinner.displayName = 'RoundedCTASpinner';

const SpinnerContainer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;
SpinnerContainer.displayName = 'SpinnerContainer';

const SwapAllButton = styled(PrimaryButton)`
  margin-left: 1rem;
  width: 13.75rem;
  margin-right: 1.5rem;
  margin-bottom: 1.5rem;
`;

SwapAllButton.displayName = 'SwapAllButton';

const SwapOneButton = styled(SecondaryButton)`
  width: 13.75rem;
  margin-left: 1.5rem;
  margin-bottom: 1.5rem;
`;

SwapOneButton.displayName = 'SwapOneButton';
